import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboard, cuentas, movimientos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { exportPLToPDF } from '../lib/pdfExport';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronRight, 
  FileDown,
  TrendingUp,
  TrendingDown,
  Calendar,
  Loader2
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

export default function EstadoResultados() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [cuentasList, setCuentasList] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('consolidado');
  const [data, setData] = useState(null);
  const [ingresosOpen, setIngresosOpen] = useState(true);
  const [egresosOpen, setEgresosOpen] = useState(true);
  
  // Date filters - Dynamic based on actual data
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedMes, setSelectedMes] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedPeriodo, setSelectedPeriodo] = useState('1');
  const [selectedSubPeriodo, setSelectedSubPeriodo] = useState('1');
  
  // Dynamic date range from actual movements
  const [availableYears, setAvailableYears] = useState([]);

  // Period options
  const periodoOptions = [
    { value: '1', label: 'Mes' },
    { value: '2', label: 'Bimestre' },
    { value: '3', label: 'Trimestre' },
    { value: '4', label: 'Cuatrimestre' },
    { value: '6', label: 'Semestre' },
    { value: '12', label: 'Año' },
  ];

  // Sub-period options based on selected period
  const getSubPeriodoOptions = () => {
    switch (selectedPeriodo) {
      case '2': return [
        { value: '1', label: 'Bim 1 (Ene-Feb)' }, { value: '2', label: 'Bim 2 (Mar-Abr)' },
        { value: '3', label: 'Bim 3 (May-Jun)' }, { value: '4', label: 'Bim 4 (Jul-Ago)' },
        { value: '5', label: 'Bim 5 (Sep-Oct)' }, { value: '6', label: 'Bim 6 (Nov-Dic)' },
      ];
      case '3': return [
        { value: '1', label: 'T1 (Ene-Mar)' }, { value: '2', label: 'T2 (Abr-Jun)' },
        { value: '3', label: 'T3 (Jul-Sep)' }, { value: '4', label: 'T4 (Oct-Dic)' },
      ];
      case '4': return [
        { value: '1', label: 'C1 (Ene-Abr)' }, { value: '2', label: 'C2 (May-Ago)' },
        { value: '3', label: 'C3 (Sep-Dic)' },
      ];
      case '6': return [
        { value: '1', label: 'S1 (Ene-Jun)' }, { value: '2', label: 'S2 (Jul-Dic)' },
      ];
      default: return [];
    }
  };

  // Month names in Spanish
  const monthNames = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  // Reset sub-period when period changes
  useEffect(() => {
    setSelectedSubPeriodo('1');
  }, [selectedPeriodo]);

  // Update selectedMes when year or month changes
  useEffect(() => {
    setSelectedMes(`${selectedYear}-${selectedMonth}`);
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    loadCuentas();
    loadDateRange();
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedCuenta, selectedMes, selectedPeriodo, selectedSubPeriodo, selectedYear]);

  const loadDateRange = async () => {
    try {
      const response = await movimientos.getRangoFechas();
      if (response.data?.anios_disponibles) {
        setAvailableYears(response.data.anios_disponibles);
        // Set to most recent year with data
        if (response.data.anios_disponibles.length > 0) {
          const mostRecentYear = response.data.anios_disponibles[0];
          setSelectedYear(mostRecentYear);
        }
      }
    } catch (error) {
      console.error('Error loading date range:', error);
    }
  };

  const loadCuentas = async () => {
    try {
      const response = await cuentas.getAll();
      setCuentasList(response.data);
    } catch (error) {
      console.error('Error loading cuentas:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedCuenta === 'consolidado' 
        ? { consolidado: true } 
        : { cuenta_id: selectedCuenta };
      
      // Calculate date range based on period type
      const periodoMeses = parseInt(selectedPeriodo);
      const baseYear = parseInt(selectedYear);
      
      let fechaInicio, fechaFin;
      
      if (periodoMeses === 1) {
        // Single month
        const baseMonth = parseInt(selectedMonth);
        const lastDay = new Date(baseYear, baseMonth, 0).getDate();
        fechaInicio = `${selectedYear}-${selectedMonth}-01`;
        fechaFin = `${selectedYear}-${selectedMonth}-${lastDay}`;
        params.mes = selectedMes;
      } else if (periodoMeses === 12) {
        // Full year
        fechaInicio = `${selectedYear}-01-01`;
        fechaFin = `${selectedYear}-12-31`;
        params.fecha_inicio = fechaInicio;
        params.fecha_fin = fechaFin;
      } else {
        // Bimestre, Trimestre, Cuatrimestre, Semestre
        const subPeriod = parseInt(selectedSubPeriodo);
        const startMonth = (subPeriod - 1) * periodoMeses + 1;
        const endMonth = subPeriod * periodoMeses;
        const endDay = new Date(baseYear, endMonth, 0).getDate();
        fechaInicio = `${selectedYear}-${String(startMonth).padStart(2, '0')}-01`;
        fechaFin = `${selectedYear}-${String(endMonth).padStart(2, '0')}-${endDay}`;
        params.fecha_inicio = fechaInicio;
        params.fecha_fin = fechaFin;
      }

      const [gastosRes, kpisRes] = await Promise.all([
        dashboard.getGastosPorCategoria(params),
        dashboard.getKpis(params)
      ]);

      const kpis = kpisRes.data;
      const gastos = gastosRes.data;

      const plData = {
        ingresos: [
          { label: 'Ventas y Servicios', amount: kpis?.ingresos || 0 }
        ],
        total_ingresos: kpis?.ingresos || 0,
        egresos: gastos.map(g => ({
          label: g.categoria_nombre || 'Sin clasificar',
          amount: g.total || 0
        })),
        total_egresos: kpis?.egresos || 0,
        utilidad_bruta: (kpis?.ingresos || 0) - (kpis?.egresos || 0),
        utilidad_neta: (kpis?.ingresos || 0) - (kpis?.egresos || 0),
        margen: kpis?.margen || 0
      };

      setData(plData);
    } catch (error) {
      console.error('Error loading P&L data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (data) {
      // Get empresa/cliente name from user context
      let empresaNombre = 'FinScope';
      
      if (user?.cliente_nombre) {
        empresaNombre = user.cliente_nombre;
      } else if (user?.empresa_nombre) {
        empresaNombre = user.empresa_nombre;
      } else if (user?.role === 'super_admin') {
        empresaNombre = 'Todas las empresas';
      }
      
      const cuentaNombre = selectedCuenta === 'consolidado' 
        ? 'Consolidado' 
        : cuentasList.find(c => c.id === selectedCuenta)?.nombre || '';
      
      const subtitulo = selectedCuenta === 'consolidado'
        ? `Consolidado - ${empresaNombre}`
        : `${cuentaNombre} - ${empresaNombre}`;
      
      exportPLToPDF(data, subtitulo);
      toast.success('PDF generado exitosamente');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-12 w-64 mb-6 rounded"></div>
        <div className="skeleton h-96 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div data-testid="estado-resultados-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>
              Estado de Resultados
            </h1>
            <p className="hidden sm:block">
              Análisis detallado de ingresos y egresos (P&L)
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn" className="btn-icon-only sm:w-auto">
              <FileDown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">PDF</span>
            </Button>
            <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
              <SelectTrigger className="w-32 sm:w-48" data-testid="cuenta-selector">
                <SelectValue placeholder="Cuenta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consolidado">Consolidado</SelectItem>
                {cuentasList.map((cuenta) => (
                  <SelectItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Date Filters Card */}
      <div className="page-content">
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Year Selector */}
              <div>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-24" data-testid="year-selector">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.length > 0 ? (
                      availableYears.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))
                    ) : (
                      Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() - i)).map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Month Selector - ONLY show when "Mes" is selected */}
              {selectedPeriodo === '1' && (
                <div>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="w-32" data-testid="month-selector">
                      <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                      <SelectValue placeholder="Mes" />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((month) => (
                        <SelectItem key={month.value} value={month.value}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Period Selector */}
              <div>
                <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
                  <SelectTrigger className="w-32" data-testid="periodo-selector">
                    <SelectValue placeholder="Periodo" />
                  </SelectTrigger>
                  <SelectContent>
                    {periodoOptions.map((periodo) => (
                      <SelectItem key={periodo.value} value={periodo.value}>
                        {periodo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Sub-Period Selector */}
              {['2', '3', '4', '6'].includes(selectedPeriodo) && (
                <div>
                  <Select value={selectedSubPeriodo} onValueChange={setSelectedSubPeriodo}>
                    <SelectTrigger className="w-36" data-testid="subperiodo-selector">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSubPeriodoOptions().map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="text-sm text-muted-foreground ml-2">
                {selectedPeriodo === '12' ? (
                  <span>Año {selectedYear}</span>
                ) : selectedPeriodo === '1' ? (
                  <span>{monthNames.find(m => m.value === selectedMonth)?.label} {selectedYear}</span>
                ) : (
                  <span>{getSubPeriodoOptions().find(o => o.value === selectedSubPeriodo)?.label} {selectedYear}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Summary Cards */}
        <div className="summary-cards-grid mb-6">
          <Card className="bg-green-50 border-green-200 summary-card">
            <CardContent className="p-0">
              <p className="text-xs sm:text-sm text-green-600">Ingresos</p>
              <p className="text-lg sm:text-2xl font-bold text-green-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatCurrency(data?.total_ingresos)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200 summary-card">
            <CardContent className="p-0">
              <p className="text-xs sm:text-sm text-red-600">Egresos</p>
              <p className="text-lg sm:text-2xl font-bold text-red-700 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatCurrency(data?.total_egresos)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 summary-card">
            <CardContent className="p-0">
              <p className="text-xs sm:text-sm text-blue-600">Utilidad</p>
              <p className={`text-lg sm:text-2xl font-bold tabular-nums ${data?.utilidad_neta >= 0 ? 'text-blue-700' : 'text-red-700'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {formatCurrency(data?.utilidad_neta)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200 summary-card">
            <CardContent className="p-0">
              <p className="text-xs sm:text-sm text-purple-600">Margen</p>
              <p className={`text-lg sm:text-2xl font-bold ${data?.margen >= 0 ? 'text-purple-700' : 'text-red-700'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {data?.margen || 0}%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* P&L Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ fontFamily: 'Outfit, sans-serif' }}>Estado de Resultados Detallado</span>
              <Badge variant="outline">
                {selectedCuenta === 'consolidado' ? 'Consolidado' : cuentasList.find(c => c.id === selectedCuenta)?.nombre}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {/* Ingresos Section */}
              <Collapsible open={ingresosOpen} onOpenChange={setIngresosOpen}>
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <div className="flex items-center gap-2">
                      {ingresosOpen ? <ChevronDown className="w-5 h-5 text-green-600" /> : <ChevronRight className="w-5 h-5 text-green-600" />}
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="font-bold text-green-800">INGRESOS</span>
                    </div>
                    <span className="font-bold text-green-700">{formatCurrency(data?.total_ingresos)}</span>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="ml-8 mt-2 space-y-1">
                    {data?.ingresos?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-green-50/50 rounded border-l-2 border-green-300">
                        <span className="text-gray-700">{item.label}</span>
                        <span className="text-green-600 font-medium">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Egresos Section */}
              <Collapsible open={egresosOpen} onOpenChange={setEgresosOpen}>
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                    <div className="flex items-center gap-2">
                      {egresosOpen ? <ChevronDown className="w-5 h-5 text-red-600" /> : <ChevronRight className="w-5 h-5 text-red-600" />}
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      <span className="font-bold text-red-800">EGRESOS</span>
                    </div>
                    <span className="font-bold text-red-700">{formatCurrency(data?.total_egresos)}</span>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="ml-8 mt-2 space-y-1">
                    {data?.egresos?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-50/50 rounded border-l-2 border-red-300">
                        <span className="text-gray-700">{item.label}</span>
                        <span className="text-red-600 font-medium">{formatCurrency(item.amount)}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Utilidad Bruta */}
              <div className="flex items-center justify-between p-3 bg-blue-100 rounded-lg mt-4">
                <span className="font-bold text-blue-800">UTILIDAD BRUTA</span>
                <span className={`font-bold ${data?.utilidad_bruta >= 0 ? 'text-blue-700' : 'text-red-600'}`}>
                  {formatCurrency(data?.utilidad_bruta)}
                </span>
              </div>

              {/* Utilidad Neta */}
              <div className="flex items-center justify-between p-4 bg-gray-800 rounded-lg text-white">
                <span className="font-bold text-lg">UTILIDAD NETA</span>
                <div className="flex items-center gap-3">
                  <span className={`font-bold text-lg ${data?.utilidad_neta >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                    {formatCurrency(data?.utilidad_neta)}
                  </span>
                  <Badge className="bg-white/20 text-white">
                    {data?.margen || 0}%
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legend - Hidden on mobile */}
        <div className="mt-4 hidden sm:flex items-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <ChevronRight className="w-4 h-4" />
            <span>Clic para expandir/colapsar secciones</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span>Ingresos</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-red-500" />
            <span>Egresos</span>
          </div>
        </div>
      </div>
    </div>
  );
}
